import React from "react";
import { Button, HStack, Menu, MenuButton, MenuItem, MenuList, useDisclosure, useToast } from "@chakra-ui/react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { BsPlusLg, BsThreeDotsVertical } from "react-icons/bs";
import RiskReviewEmptyState from "../components/common/RiskReviewEmptyState";
import RiskReviewErrorState from "../components/common/RiskReviewErrorState";
import RiskReviewLoadingState from "../components/common/RiskReviewLoadingState";
import RiskReviewPageShell from "../components/common/RiskReviewPageShell";
import RiskReviewBulkUploadTemplateModal from "../components/templates/RiskReviewBulkUploadTemplateModal";
import RiskReviewTemplateTable from "../components/templates/RiskReviewTemplateTable";
import { riskReviewKeys } from "../constants/riskReviewQueryKeys";
import { useRiskReviewTemplates } from "../hooks/useRiskReviewTemplates";
import riskReviewService from "../services/riskReviewService";
import { getRiskReviewErrorMessage } from "../utils/riskReviewFormatters";
import { toRiskReviewTemplatePayload } from "../utils/riskReviewTemplateImportParser";

const RiskReviewTemplatesPage = () => {
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const bulkUpload = useDisclosure();
  const query = useRiskReviewTemplates({});
  const templates = Array.isArray(query.data) ? query.data : [];

  const versionMutation = useMutation({
    mutationFn: (templateId) => riskReviewService.createTemplateVersion(templateId),
    onSuccess: (version, templateId) => {
      toast({ title: "Template version created", status: "success" });
      queryClient.invalidateQueries({ queryKey: riskReviewKeys.templates() });
      navigate(`/risk-review/templates/${templateId}/versions/${version._id}`);
    },
    onError: (error) => toast({ title: getRiskReviewErrorMessage(error), status: "error" }),
  });

  const importMutation = useMutation({
    mutationFn: (template) =>
      riskReviewService.createTemplate(toRiskReviewTemplatePayload(template)),
    onSuccess: (template) => {
      toast({ title: "Template imported as draft", status: "success" });
      queryClient.invalidateQueries({ queryKey: riskReviewKeys.templates() });
      bulkUpload.onClose();
      navigate(`/risk-review/templates/${template._id}`);
    },
    onError: (error) => toast({ title: getRiskReviewErrorMessage(error), status: "error" }),
  });

  const seedMutation = useMutation({
    mutationFn: riskReviewService.seedDefaultTemplate,
    onSuccess: (result) => {
      const template = result?.template;
      toast({
        title: result?.created
          ? "Tenant Risk Review template seeded"
          : "Tenant Risk Review template already exists",
        status: "success",
      });
      queryClient.invalidateQueries({ queryKey: riskReviewKeys.templates() });
      if (template?._id) navigate(`/risk-review/templates/${template._id}`);
    },
    onError: (error) => toast({ title: getRiskReviewErrorMessage(error), status: "error" }),
  });

  return (
    <RiskReviewPageShell
      title="Risk Review Templates"
      description="Configure reusable review structures, fields, rules and versions."
      actions={
        <HStack>
          <Button size="sm" variant="outline" onClick={bulkUpload.onOpen}>
            Bulk Upload
          </Button>
          <Button as={RouterLink} to="/risk-review/templates/new" size="sm" colorScheme="blue" leftIcon={<BsPlusLg />}>
            Create Template
          </Button>
          <Menu>
            <MenuButton as={Button} size="sm" variant="ghost" aria-label="Admin actions">
              <BsThreeDotsVertical />
            </MenuButton>
            <MenuList>
              <MenuItem isDisabled={seedMutation.isPending} onClick={() => seedMutation.mutate()}>
                Seed Tenant Template
              </MenuItem>
            </MenuList>
          </Menu>
        </HStack>
      }
    >
      {query.isLoading ? <RiskReviewLoadingState rows={8} /> : null}
      {query.isError ? <RiskReviewErrorState error={query.error} onRetry={query.refetch} /> : null}
      {query.isSuccess && templates.length ? (
        <RiskReviewTemplateTable templates={templates} onCreateVersion={(id) => versionMutation.mutate(id)} />
      ) : null}
      {query.isSuccess && !templates.length ? (
        <RiskReviewEmptyState title="No templates found" message="No Risk Review templates have been configured yet." />
      ) : null}
      <RiskReviewBulkUploadTemplateModal
        isOpen={bulkUpload.isOpen}
        onClose={bulkUpload.onClose}
        isImporting={importMutation.isPending}
        onImport={(template) => importMutation.mutate(template)}
      />
    </RiskReviewPageShell>
  );
};

export default RiskReviewTemplatesPage;
